import { type DependencyList, useCallback } from 'react'

type NonNullableTuple<Actions extends readonly unknown[]> = {
	[Index in keyof Actions]: NonNullable<Actions[Index]>
}

/**
 * A hook that creates a memoized callback only when potentially-undefined action(s) are available.
 *
 * This is useful when you have conditional action(s) (e.g., from useCustomMutation which returns
 * undefined when permissions aren't met) and want to create a callback that uses them.
 *
 * Instead of:
 * ```typescript
 * const handleAction = useCallback((payload) => {
 *   if (!action) return
 *   // ... use action
 * }, [action, ...deps])
 * return action && handleAction
 * ```
 *
 * You can write:
 * ```typescript
 * const handleAction = useMaybeCallback((action, payload) => {
 *   // ... use action (guaranteed non-null)
 * }, [...deps], action)
 * ```
 *
 * When the callback depends on several potentially-undefined values, pass them as an array. The
 * callback only runs once all of them are present, and receives them as leading non-null arguments:
 * ```typescript
 * const handleAction = useMaybeCallback(
 *   (action, id, name, payload) => {
 *     // ... action, id and name are all guaranteed non-null
 *   },
 *   [...deps],
 *   [maybeAction, maybeId, maybeName],
 * )
 * ```
 *
 * An array is always treated as a list of actions: every item must be present and they are spread
 * into the callback. A lone action that is itself an array cannot be passed through as a single
 * argument — wrap it in another array (`[maybeArray]`) to gate on the array being present.
 *
 * When combining an array of actions with extra call-time arguments, the type arguments must be
 * given explicitly: TS cannot infer the trailing args off a spread action tuple, so without
 * `useMaybeCallback<[...Actions], [...Args], Result>(...)` those args silently degrade to `unknown`.
 *
 * @param callback - A function that receives the non-null action(s) and any additional arguments
 * @param deps - Additional dependencies for the callback (the action(s) are automatically included)
 * @param action - The potentially-undefined action(s) to wrap (a single value or an array of values)
 * @returns undefined if any action is falsy, otherwise a memoized callback
 */
function useMaybeCallback<const Actions extends readonly unknown[], Args extends readonly unknown[], Result = void>(
	// For react-hooks/exhaustive-deps linting, we need the callback and deps before the action(s)
	callback: (...args: [...NonNullableTuple<Actions>, ...Args]) => Result,
	deps: DependencyList,
	actions: readonly [...Actions],
): ((...args: Args) => Result) | undefined
function useMaybeCallback<Action, Args extends readonly unknown[], Result>(
	callback: (action: NonNullable<Action>, ...args: Args) => Result,
	deps: DependencyList,
	action: Action,
): ((...args: Args) => Result) | undefined
function useMaybeCallback(
	// The implementation widens over both overloads, which only `any` can satisfy contravariantly.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	callback: (...args: any[]) => any,
	deps: DependencyList,
	action: unknown,
): ((...args: readonly unknown[]) => unknown) | undefined {
	const actions = Array.isArray(action) ? action : [action]
	const memoizedCallback = useCallback(
		// We know the action(s) are non-null here because of the check below.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		(...args: readonly unknown[]) => callback(...actions, ...args),
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[...actions, ...deps],
	)

	return actions.every(Boolean) ? memoizedCallback : undefined
}

export { useMaybeCallback }
